/**
 * Locale strings.
 *
 * The UI ships in English only, but no string is written inline. Retrofitting
 * i18n means touching every component; doing it now costs one indirection.
 * Locale codes are BCP-47 and stored as codes, never as display names.
 */

export const DEFAULT_LOCALE = 'en'

const strings = {
  en: {
    // standards: const-duplicated-literal exempt -- the product name in user-facing copy, which is where a product name belongs. The
    // other home is SITE_NAME in the page generator, and neither language can read the
    // other's constant.
    'app.name': 'SourceText',
    'app.tagline': 'Answers grounded in the text. Every claim marked, every citation live.',
    'app.readTheText': 'Read the text',
    'chat.placeholder': 'Ask about a passage, a word, or what the text actually says…',
    'chat.send': 'Ask',
    'chat.stop': 'Stop',
    'chat.emptyTitle': 'Ask what the text says.',
    'chat.emptyBody':
      'Answers here come only from the text this tool can retrieve — never from memory. Where the text does not settle something, it will say so.',
    'chat.retrieving': 'Consulting the corpus…',
    'beta.heading': 'Beta — your data may be deleted at any time.',
    'beta.body':
      'SourceText is still in development. Saved findings, themes and threads ' +
      'can be reset or removed without notice, and no backup is kept. Export ' +
      'your study from the workspace to keep your own copy.',
    'chat.thinking': 'Working…',
    'chat.writing': 'Writing the answer…',
    // One per corpus tool. A reader should see what is being consulted, not
    // the function name — and should see the line change as retrieval moves on.
    'tool.get_passage': 'Reading the passage…',
    'tool.search': 'Searching the text…',
    'tool.compare_translations': 'Comparing translations…',
    'tool.lexicon': 'Checking the lexicon…',
    'tool.words_in_verse': 'Reading the original words…',
    'tool.cross_references': 'Finding related passages…',
    // Research mode. The Danish, for the day a second locale arrives, is
    // 'Kun Bibelen' / 'Kun loven' / 'Research' — recorded here rather than lost,
    // because this file holds one locale today and inventing a second is not
    // this change.
    //
    // The grounded label names what the answer is limited to, per the corpus's
    // reference grammar (Jack, 2026-10-18): "Text only" read as plain text with
    // no formatting, and "Bible only" is what a reader of scripture means. The
    // grammar is corpus data, so no corpus code is named here; `mode.grounded`
    // is for a grammar this file does not know yet.
    'mode.legend': 'How this question is answered',
    'mode.grounded': 'From the text',
    'mode.grounded.biblical': 'Bible only',
    'mode.grounded.statute': 'Law only',
    'mode.research': 'Research',
    'mode.groundedHint': 'Answer only from the text this tool can retrieve.',
    'mode.researchHint':
      'Also report what commentaries, traditions and scholarship hold — labelled as such, and never mistaken for the text.',
    'mode.badge': 'Research mode',
    'mode.badgeNote':
      'Sources beyond the text are reported here, and marked. What the text says is still cited.',
    'chat.groundedIn': 'Grounded in',
    'chat.versionRow': 'Versions read',
    'chat.addVersion': 'Add a translation',
    'chat.removeVersion': 'Remove {name}',
    'chat.pinnedVersion': '{name}, always read',
    'chat.activeNote': 'About the versions used',
    'chat.beyondText': 'goes beyond the text',
    'chat.evidence': 'See evidence',
    'chat.remaining': '{count} questions left today',
    'chat.capReached':
      'You have used today’s questions. The limit resets at midnight UTC.',
    'chat.capSignUpOffer': 'A free account gets {count} questions a day.',
    'chat.rateLimited': 'That was a little quick. Wait a moment and ask again.',
    'chat.openAccount': 'Open your account',
    'account.signIn': 'Sign in',
    'account.signOut': 'Sign out',
    'account.email': 'Email',
    'account.password': 'Password',
    'account.currentPassword': 'Current password',
    'account.newPassword': 'New password',
    'account.changePassword': 'Change password',
    'account.mustChange':
      'This password was issued to you by someone else. Choose your own before going on.',
    'account.noSignup':
      'Accounts are created for you on this deployment — there is no sign-up.',
    'account.signUpPrompt': 'New here?',
    'account.signUp': 'Create an account',
    'account.forgotPassword': 'Forgotten your password?',
    'account.newConfirmationLink': 'Send me a new confirmation link',
    'account.manage': 'Account settings ↗',
    'account.myFindings': 'My findings',
    'account.questionsLeft': '{remaining} of {limit} questions left today',
    'account.questionsUnlimited': 'No daily question limit',
    'account.storingOn': 'Saving findings: on',
    'account.storingOff': 'Saving findings: downloads only',
    'account.export': 'Download everything',
    'account.deleteAccount': 'Delete account',
    'account.deleteWarning':
      'Deleting your account erases every finding you have saved. It cannot be undone.',
    'account.typeEmailToConfirm': 'Type your email address to confirm',
    'consent.title': 'Before this is saved',
    'consent.body':
      'Saving a finding stores your question, the answer and its claims on our server. What you study is personal, and under GDPR it counts as special-category data — so we ask outright rather than assume.',
    'consent.alternative':
      'You can decline and keep using the download instead: the file is identical and never leaves your device.',
    'consent.agree': 'Save it to my account',
    'consent.decline': 'Download it instead',
    'findings.title': 'Findings in this corpus',
    'findings.empty': 'Nothing saved yet.',
    'findings.claims': '{count} claims',
    'findings.delete': 'Delete',
    'findings.saved': 'Saved to your account.',
    'findings.savedCount': 'Kept {count} as findings.',
    'findings.new': 'Write a finding',
    'findings.edit': 'Edit',
    'findings.missing': 'No finding with the id “{slug}” in this corpus.',
    'findings.retireInstead': 'Retire it instead? It stays, marked withdrawn, and everything pointing at it keeps working.',
    'findings.column.claim': 'Claim',
    'findings.column.status': 'Status',
    'findings.column.kind': 'Kind',
    'findings.column.themes': 'Themes',
    'findings.column.updated': 'Updated',
    'findings.filter.claim': 'Filter claims',
    'findings.filter.status': 'Filter by status',
    'findings.filter.kind': 'Filter by kind',
    'findings.filter.themes': 'Filter by theme',
    'filter.any': 'Any',
    'pager.previous': 'Previous',
    'pager.next': 'Next',
    'pager.position': 'Page {current} of {pages}',
    // How settled a claim is. The framework's own vocabulary, rendered as
    // words a reader would use — the stored value stays the framework's code.
    'findings.status.hypothesis': 'Hypothesis',
    'findings.status.developing': 'Developing',
    'findings.status.established': 'Established',
    'findings.status.firm': 'Firm',
    'findings.status.contested': 'Contested',
    'findings.status.retired': 'Retired',
    // What kind of claim it is — independent of how settled it is.
    'claim.type.asserted': 'The text asserts it',
    'claim.type.synthesized': 'Synthesised',
    'claim.type.open': 'Open',
    // Where a claim came from. Never inferred from how confident an answer
    // sounded — `mixed` records that a model helped, and only the reader can
    // change it.
    //
    // What is *stored* is an author's name; `origin.own` is what that name is
    // rendered as when it is the reader's own. Somebody else's name is shown
    // as itself, because on their finding it is the whole point.
    'origin.own': 'My own reading',
    'origin.reused': 'Reused from elsewhere',
    'origin.mixed': 'Model-assisted',
    'origin.byAuthor': 'A reading by {author}',
    'edge.builds_on': 'Builds on',
    'edge.tension_with': 'In tension with',
    'edge.related': 'Related',
    'card.finding': 'Finding',
    // The citation report. "Unverified" is never phrased as a fault: it means
    // we hold no licence to the translation, which is a fact about us.
    'citations.heading': 'Citations',
    'citations.recheck': 'Check again',
    'citations.none': 'No citations to check yet.',
    'citations.allResolve': '{count} citations — all resolve',
    'citations.needsReview': '{count} need a look',
    'citations.someUnverified': '{count} could not be checked',
    'citations.accept': 'Accept as it is',
    'citations.openPassage': 'Open the passage',
    'citations.verdict.resolves': 'Resolves',
    'citations.verdict.unknown_book': 'Not a book this corpus knows',
    'citations.verdict.out_of_range': 'That chapter or verse does not exist here',
    'citations.verdict.quote_matches': 'Matches the text',
    'citations.verdict.quote_unverified': 'Not checked',
    'citations.verdict.quote_no_match': 'Not found where it is cited',
    'citations.verdict.unknown_version': 'Unknown translation code',
    'review.title': 'Citations to review',
    'review.menu': 'Review study ({count})',
    'review.empty': 'Nothing needs a look. Everything either resolves or has been accepted.',
    'review.position': '{current} of {total}',
    'review.fix': 'Fix it',
    'review.accept': 'Accept and move on',
    'review.skip': 'Skip for now',
    'review.importedNote':
      'This finding was imported from {author} and cannot be edited. Accepting records that you have seen it.',
    'imports.from': 'From {author}',
    'imports.count': '{count} findings',
    'imports.open': 'Open',
    'imports.remove': 'Remove this import',
    'imports.claim': 'These are mine',
    'imports.confirmClaim':
      'Make the {count} findings from {author} your own? Do this only if {author} is you — for example your own study corpus brought in as a pack. They become editable, go out in your packs and export as your work. Each keeps the origin it was written with.',
    'imports.claimed': '{count} findings from {author} are now your own.',
    'imports.claimFailed': 'The findings were not made your own',
    'imports.readOnly': 'Read-only. Never published in your packs.',
    'imports.readOnlyLong':
      'These are another author’s findings, held as reference. They cannot be edited and never go out in your packs — but you can build on one by writing your own finding that points at it.',
    'imports.confirmRemove':
      'Remove everything imported from {author}? Edges pointing at those findings will stop resolving.',
    // The two inbound doors, and the copy leans on the difference between them
    // rather than explaining it: a file is taken once, a feed keeps arriving.
    'imports.bring': 'Bring findings in',
    'imports.uploadHeading': 'Upload a pack',
    'imports.uploadNote':
      'A pack file from anywhere — including one published by the study framework. It is a snapshot: what you upload is what you get, and later changes by its author will not reach you.',
    'imports.uploadChoose': 'Choose a pack file',
    'imports.upload': 'Upload',
    'imports.uploaded':
      'Imported {count} findings from {author}. They are read-only, and yours to build on.',
    'imports.followHeading': 'Follow a feed',
    'imports.followNote':
      'A feed on this site, so its updates keep arriving. When the author revises a finding you will get the revision; if you have adopted one, you will be told its source moved.',
    'imports.followUrl': 'Feed address',
    'imports.followPack': 'Which pack',
    'imports.follow': 'Follow',
    'imports.followed': 'Following {author} — {pack}. Their changes will arrive from now on.',
    'imports.following': 'Feeds you follow',
    'imports.unfollow': 'Unfollow',
    'imports.upToDate': 'up to date at {version}',
    'imports.behind': 'updating to {version}',
    'imports.neverSynced': 'not pulled yet',
    'imports.confirmUnfollow':
      'Stop following {author}? You keep everything already received — this only stops new changes arriving.',
    'imports.consent':
      'Store these findings in my study. They are a record of religious study, which I am asking SourceText to keep.',
    'imports.needsConsent': 'Tick the box to store study content before importing.',
    'imports.uploadDrop': 'or drop it here',
    'imports.uploadChange': 'click to choose another',
    'imports.uploadNoFile': 'Choose a pack file first — the .zip you exported or were sent.',
    'imports.uploading': 'Uploading…',
    'imports.uploadSending': 'Sending the file… {percent}%',
    'imports.uploadImporting':
      'File received. Checking and storing every finding — a whole corpus can take a minute.',
    'imports.uploadFailed': 'The pack was not imported',
    'imports.openImported': 'Open them',
    'imports.megabytes': '{size} MB',
    'imports.followUrlExample': 'https://bible.sourcetext.ai/packs/author/feed.json',
    'imports.followIncomplete': 'Fill in both the feed address and which pack to follow.',
    'imports.followFailed': 'The feed was not followed',
    // Adoption: the one way out of the quarantine, and the framework's own
    // instruction with the copying done. The wording is deliberately about
    // *believing* rather than about taking — the copy keeps its author's name,
    // and the note says so before the copy exists rather than after.
    'adopt.heading': 'Adopt this finding',
    'adopt.explanation':
      'Make this your own finding — a copy you can edit, link and publish. It stays credited to {author}, because deciding you believe a reading does not make the reasoning yours.',
    'adopt.statusLabel': 'How settled do you hold it?',
    'adopt.statusUnchosen': 'Choose…',
    'adopt.confirm': 'I believe this to be true',
    'adopt.already': 'Adopted',
    'adopt.retired':
      'Its author withdrew this claim, so there is nothing to adopt. If you hold it yourself, write your own finding saying so.',
    'adopt.failed': 'That could not be adopted.',
    // A reference whose finding is gone. Shown rather than hidden: the link was
    // the reader's own work, and quietly removing it would lose the record that
    // this claim ever stood on anything.
    'references.deleted': 'finding deleted',
    'review.dangling': 'References that point at nothing',
    'review.danglingNote':
      'These point at findings that have been deleted — usually an import you put back down. Nothing is lost, and nothing needs accepting: edit the finding to remove or repoint the reference, or leave it as a record of what it once stood on.',
    'review.danglingIn': '{field} → {target}',
    // A claim the reader adopted whose original has since been revised. Worded
    // as information rather than as a problem: nothing is wrong with their
    // finding, and whether the revision changes anything is theirs to judge.
    'review.moved': 'Claims whose source has changed',
    'review.movedNote':
      'You adopted these, and their authors have since revised the findings you copied. Your claims are untouched — this only says the reading you based them on has moved. Open the original to see what it says now.',
    'review.movedFrom': 'adopted from {author}',
    'review.movedOpen': 'Open the original',
    'review.movedSeen': 'I have looked',
    'editor.new': 'New finding',
    'editor.edit': 'Edit finding',
    'editor.claim': 'The claim',
    'editor.claimHint': 'State it as a sentence. A claim needing two sentences is two claims.',
    'editor.title': 'Title',
    'editor.status': 'How settled',
    'editor.kind': 'What kind of claim',
    'editor.origin': 'Where it came from',
    'editor.unset': 'Not stated',
    'editor.themes': 'Themes',
    'editor.themesHint': 'Comma-separated. A theme you name here is created if it is new.',
    'editor.keywords': 'Keywords',
    'editor.scripture': 'Scripture',
    'editor.scriptureHint': 'Comma-separated references. These are what gets checked against the text.',
    'editor.sources': 'Sources',
    'editor.body': 'The argument',
    'editor.slug': 'Id',
    'editor.slugHint': 'The filename and the [[link]] target. Left blank, it comes from the claim.',
    'editor.slugPublished':
      'This finding has already been published in a pack. Changing its id breaks every link to it, including in other people’s corpora.',
    'editor.commaSeparated': 'Comma-separated.',
    'editor.titleRequired': 'A finding needs a claim stated as its title.',
    'editor.save': 'Save',
    'editor.cancel': 'Cancel',
    // A save refused because somebody else saved first. The reader's typing is
    // never thrown away for them: they see the other version and choose.
    'conflict.heading': 'This was saved somewhere else while you were editing',
    'conflict.when': 'The other save was made {when}. Your changes are still in the form.',
    'conflict.theirs': 'What the other save says',
    'conflict.same': 'Nothing you can see in this form differs — only parts it does not show.',
    'conflict.useTheirs': 'Use their version',
    'conflict.keepMine': 'Keep mine',
    'conflict.keepMineHint': 'Keeping yours replaces theirs when you press Save.',
    'conflict.reload': 'Reload to see the current version, then make your change again.',
    'curation.title': 'Which claims are worth keeping?',
    'curation.note':
      'Each one becomes its own finding, with the question and answer kept inside it. They arrive as hypotheses: how settled a claim is stays yours to grant.',
    'curation.keep': 'Keep {count}',
    'curation.downloadInstead': 'Download instead',
    'themes.title': 'Themes',
    'themes.new': 'New theme',
    'themes.edit': 'Edit theme',
    'themes.empty': 'No themes yet. One is created whenever you name it on a finding.',
    'themes.count': '{count} findings',
    'themes.category': 'Category',
    'themes.hub': 'What this theme is for',
    'themes.hubHint': 'The hub document. A theme used without one is flagged when you export.',
    'themes.confirmDelete': 'Delete this theme? The findings filed under it stay; they are simply unfiled.',
    'themes.note': 'A theme is a bucket with a hub document explaining what belongs in it.',
    'threads.title': 'Open questions',
    'threads.new': 'New question',
    'threads.edit': 'Edit question',
    'threads.empty': 'No open questions recorded.',
    'threads.question': 'The question',
    'threads.related': 'Findings it stands on',
    'threads.note':
      'A thread is a question being worked, not a claim. Recording one is the honest alternative to a finding stated too firmly.',
    'threads.confirmDelete': 'Delete this open question?',
    'threads.status.open': 'Open',
    'threads.status.deferred': 'Deferred',
    'threads.status.resolved': 'Resolved',
    'packs.title': 'Packs',
    'packs.new': 'New pack',
    'packs.edit': 'Edit pack',
    'packs.empty': 'No packs yet.',
    'packs.note':
      'A pack is a selection of your findings, published as a file anyone can subscribe to with the study framework’s own tooling.',
    'packs.needsName':
      'Choose the name you publish under before publishing. It becomes part of the address and a folder on every subscriber’s machine.',
    'packs.needsAddon':
      'Publishing a new version needs the Feed publishing add-on. Versions you have already published stay in your feed.',
    'packs.feedIs': 'Your feed address is',
    // The outbound door. Publishing produced an address and a file that nobody
    // could reach: the feed URL is what you send a friend, and the download is
    // what you hand to someone using the study framework.
    'packs.feedShare':
      'Send this to anyone who wants your findings. It works in SourceText and in the study framework’s own tooling.',
    'packs.download': 'Download {version}',
    'packs.versions': 'Published versions',
    'packs.count': '{count} findings',
    'packs.license': 'Licence',
    'packs.homepage': 'Homepage',
    'packs.description': 'What is in it',
    'packs.choose': 'Findings to include',
    'packs.publish': 'Publish',
    'packs.published': 'published {version}',
    'packs.unpublished': 'not published yet',
    'packs.confirmPublish':
      'Publishing makes these {count} findings public at your feed address. Anyone who knows it can download them, and this version’s file never changes afterwards. Publish?',
    'packs.confirmDelete':
      'Delete this pack? People who already pulled it keep their copy; what ends is the offer.',
    'output.empty': 'Passages and evidence you open will appear here.',
    'output.back': 'Back',
    'output.close': 'Close',
    'output.expand': 'Expand',
    'output.collapse': 'Collapse',
    'card.passage': 'Passage',
    'card.passagePage': 'Open its page to read on or share',
    'card.evidence': 'Evidence',
    'card.lexicon': 'Lexicon',
    'evidence.status.text-asserted': 'The text says this',
    'evidence.status.inferred': 'Inferred from the text',
    'evidence.status.open': 'The text does not settle this',
    'evidence.basis.text': 'from the text',
    'evidence.basis.lexicon': 'from a lexicon',
    'evidence.basis.commentary': 'from commentary',
    'evidence.basis.synthesis': 'synthesis',
    'evidence.references': 'References',
    'evidence.versions': 'Read in',
    'evidence.none': 'This answer recorded no claims.',
    'evidence.save': 'Save finding',
    'evidence.saveNote': 'Downloads a Markdown file. Nothing is sent anywhere.',
    'lexicon.occurrences': '{count} occurrences in the corpus',
    'lexicon.caveat':
      'A lexicon reports how a word is used across the corpus. It does not settle which sense a passage carries.',
    'tier.label': 'Tier {ordinal}',
    'error.generic': 'Something went wrong. Please try again.',
    'error.notLoaded': 'This could not be loaded',
    'error.incomplete': 'The answer stopped before it finished. Please try again.',
    'chat.stopped': 'Stopped.',
    'chat.retry': 'Try again',
  },
} as const

export type StringKey = keyof (typeof strings)['en']

export function t(key: StringKey, values?: Record<string, string | number>): string {
  const template: string = strings[DEFAULT_LOCALE][key] ?? key
  if (!values) return template
  return template.replace(/\{(\w+)\}/g, (match, name: string) =>
    name in values ? String(values[name]) : match,
  )
}
